import { TextField } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { NO_VALUE_DASH } from "@ryde/shared";

import { ColorIconButton } from "../../../controls";
import {
  ICustomTranslation,
  II18nKeyEntry,
} from "./custom-translations.models";
import { CustomTranslationRowStyled } from "./custom-translations.styled";

interface ITranslationRowProps {
  row: ICustomTranslation;
  index: number;
  original?: II18nKeyEntry;
  isHighlighted?: boolean;
  onChange: (index: number, patch: Partial<ICustomTranslation>) => void;
  onRemove: (index: number) => void;
}

export const TranslationRow: FC<ITranslationRowProps> = ({
  row,
  index,
  original,
  isHighlighted,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [en, setEn] = useState(row.en);
  const [he, setHe] = useState(row.he);

  // Keep local inputs in sync when the underlying value changes externally.
  useEffect(() => {
    setEn(row.en);
  }, [row.en]);

  useEffect(() => {
    setHe(row.he);
  }, [row.he]);

  const commitEn = (): void => {
    if (en !== row.en) {
      onChange(index, { en });
    }
  };

  const commitHe = (): void => {
    if (he !== row.he) {
      onChange(index, { he });
    }
  };

  return (
    <CustomTranslationRowStyled
      data-e2e-value={row.path}
      className={isHighlighted ? "ct-row--highlight" : undefined}
    >
      <div className="ct-row__head">
        <span className="ct-row__path-value">{row.path}</span>
        <ColorIconButton
          iconType="DELETE"
          aria-label="delete"
          tooltipContent={t("systemParam.customTranslations.removeOverride")}
          onClick={() => onRemove(index)}
        />
      </div>

      <div className="ct-row__fields">
        <div className="ct-row__field">
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            size="small"
            label={t("systemParam.customTranslations.english")}
            data-e2e="custom-translation-en"
            value={en}
            error={!en.trim()}
            onChange={event => setEn(event.target.value)}
            onBlur={commitEn}
          />
          <span className="ct-row__original" title={original?.en}>
            <span className="ct-row__original-lang">
              {t("systemParam.customTranslations.original")}
            </span>{" "}
            {original?.en || NO_VALUE_DASH}
          </span>
        </div>

        <div className="ct-row__field">
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            size="small"
            label="Hebrew"
            data-e2e="custom-translation-he"
            value={he}
            error={!he.trim()}
            onChange={event => setHe(event.target.value)}
            onBlur={commitHe}
          />
          <span className="ct-row__original" title={original?.he}>
            <span className="ct-row__original-lang">Original:</span>{" "}
            {original?.he || NO_VALUE_DASH}
          </span>
        </div>
      </div>
    </CustomTranslationRowStyled>
  );
};
